export type HowWeWorkStep = {
  title: string;
  duration: string;
  description: string;
  deliverables: string[];
};

export type HowWeWorkPageContent = {
  metadata: {
    title: string;
    description: string;
  };
  hero: {
    eyebrow: string;
    title: string;
    subtitle: string;
    highlights: string[];
    primaryCta: string;
    secondaryCta: string;
    secondaryHref: string;
  };
  sections: {
    timeline: {
      title: string;
      subtitle: string;
      steps: HowWeWorkStep[];
    };
    visibility: {
      title: string;
      subtitle: string;
      metrics: Array<{ label: string; display: string; progress: number; hint: string }>;
    };
    expectations: {
      title: string;
      subtitle: string;
      cards: Array<{ title: string; description: string }>;
    };
  };
  finalCta: {
    title: string;
    subtitle: string;
    primaryLabel: string;
    qualifierChips: string[];
  };
};

export const howWeWorkPageContent = {
  sr: {
    metadata: {
      title: 'Kako krenuti sa ERV-om - od prvog kontakta do stabilnog rada',
      description:
        'Jasan onboarding tok: od uvodnog poziva i mapiranja procesa do pilota, produkcije i podrške. Bez skrivenih koraka i bez improvizacije.',
    },
    hero: {
      eyebrow: 'Uvođenje Cadrino ERV',
      title: 'Kako krenuti sa ERV-om. Predvidivo.',
      subtitle:
        'Isti premium okvir implementacije kao i na ostalim stranicama: jasan plan, jasni vlasnici i jasan kriterijum završetka u svakoj fazi.',
      highlights: [
        'jasan plan od prvog dana',
        'pilot bez velikog rizika',
        'besplatna obuka i podrška',
        '2 godine garancije na opremu',
      ],
      primaryCta: 'Zakažite uvodni poziv',
      secondaryCta: 'Pogledajte Proizvod',
      secondaryHref: '/product',
    },
    sections: {
      timeline: {
        title: 'Implementacija u 5 koraka',
        subtitle:
          'Svaki korak ima jasan ishod i merljiv kriterijum završetka. Nema "videćemo usput".',
        steps: [
          {
            title: '1) Uvodni razgovor i cilj',
            duration: 'Dan 1',
            description:
              'Definišemo poslovni cilj, kritične lokacije i rizike koji mogu da uspore implementaciju, pa dogovaramo šta mora da radi u prvih 30 dana.',
            deliverables: ['Definisan opseg po lokaciji', 'Dogovoreni KPI za prvih 30 dana', 'Plan aktivnosti po danima'],
          },
          {
            title: '2) Mapiranje procesa i podataka',
            duration: 'Dan 2-3',
            description:
              'Mapiramo zaposlene, uređaje, lokacije i pravila obrade da ERV radi prema vašoj stvarnoj organizaciji, ne po generičkom šablonu.',
            deliverables: ['Mapa polja i pravila pristupa', 'Dogovoreni eksport/API model', 'Kontrolna lista tehničke spremnosti'],
          },
          {
            title: '3) Tehnička priprema i konfiguracija',
            duration: 'Dan 4-5',
            description:
              'Postavljamo okruženje, povezujemo uređaje i zatvaramo sve blokere pre pilot rada.',
            deliverables: ['Konfigurisano ERV okruženje', 'Povezani uređaji i lokacije', 'Validirani test događaji IN/OUT'],
          },
          {
            title: '4) Pilot na ograničenom opsegu',
            duration: 'Nedelja 2',
            description:
              'Puštamo pilot na jednoj lokaciji ili timu, pratimo tačnost evidencije i pristupa i zatvaramo razlike pre produkcije.',
            deliverables: ['Pilot izveštaj sa KPI', 'Validirani sati i prava pristupa', 'Lista finalnih dorada sa rokovima'],
          },
          {
            title: '5) Produkcija i rutina tima',
            duration: 'Nedelja 3+',
            description:
              'Sistem prelazi u redovan rad bez prekida poslovanja, uz praćenje prvih obračunskih ciklusa.',
            deliverables: ['Puštanje u rad bez prekida', 'Operativni priručnik za tim', 'Plan redovne podrške i eskalacije'],
          },
        ],
      },
      visibility: {
        title: 'Šta partner i klijent vide u svakom trenutku',
        subtitle: 'Transparentan tok implementacije je razlog zašto uvođenje ide brže i sa manje stresa.',
        metrics: [
          { label: 'Jasan status koraka', display: '100%', progress: 100, hint: 'Svaki korak ima vlasnika, rok i kriterijum završetka.' },
          { label: 'Pilot bez prekida rada', display: '95%', progress: 95, hint: 'Startujemo od najmanjeg sigurnog opsega.' },
          { label: 'Vreme do stabilne rutine', display: '2–3 ned.', progress: 75, hint: 'Tipično dve do tri nedelje do pune operativne rutine.' },
        ],
      },
      expectations: {
        title: 'Šta možete da očekujete od nas',
        subtitle:
          'Od prvog sastanka do produkcije, vodimo proces tako da i operativa i menadžment imaju istu sliku napretka.',
        cards: [
          {
            title: 'Bez tehničkog žargona',
            description:
              'Svaki update je poslovno jasan: šta je završeno, šta je sledeće i da li postoji rizik.',
          },
          {
            title: 'Bez "big bang" uvođenja',
            description:
              'Krećemo od pilot opsega, validiramo tačnost, pa tek onda širimo. Tako štitimo svakodnevni rad tima.',
          },
          {
            title: 'Bez nejasne odgovornosti',
            description:
              'Za svaki korak unapred znate vlasnika sa naše i vaše strane, plus kriterijum završetka.',
          },
          {
            title: 'Besplatna obuka i podrška',
            description:
              'Obuka zaposlenih, korisnička podrška i tehnička podrška su potpuno besplatni nakon puštanja u rad.',
          },
          {
            title: '2 godine garancije na opremu',
            description:
              'Sva oprema koju uvodimo u okviru projekta ima 24 meseca garancije, uz jasan servisni tok.',
          },
        ],
      },
    },
    finalCta: {
      title: 'Ako želite implementaciju sa jasnim rokovima i bez improvizacije, tu smo.',
      subtitle:
        'Pošaljite vaš scenario i dobijate konkretan plan u 48h: opseg pilota, vremenski plan i listu tehničkih preduslova.',
      primaryLabel: 'Zakažite uvodni poziv',
      qualifierChips: ['Broj lokacija i smena', 'Besplatna obuka i podrška', '2 godine garancije na opremu'],
    },
  },
  en: {
    metadata: {
      title: 'How to get started with ERV - from first contact to stable go-live',
      description:
        'A clear onboarding flow from discovery and process mapping to pilot, production rollout, and ongoing support.',
    },
    hero: {
      eyebrow: 'Cadrino ERV Rollout',
      title: 'How to get started with ERV. Predictable.',
      subtitle:
        'The same premium rollout framework used across the site: clear plan, clear owners, and clear completion criteria in every phase.',
      highlights: ['clear plan from day one', 'low-risk pilot', 'free training and support', '2-year equipment warranty'],
      primaryCta: 'Book a discovery call',
      secondaryCta: 'See Product',
      secondaryHref: '/product',
    },
    sections: {
      timeline: {
        title: 'Implementation in 5 steps',
        subtitle: 'Each step has a measurable outcome and completion criteria, so execution stays predictable.',
        steps: [
          {
            title: '1) Discovery and target state',
            duration: 'Day 1',
            description:
              'We define your target state, critical locations, and rollout risks, then align success criteria for the first 30 days.',
            deliverables: ['Location-based scope', 'First-30-day KPI alignment', 'Day-by-day execution plan'],
          },
          {
            title: '2) Process and data mapping',
            duration: 'Day 2-3',
            description:
              'We map employees, devices, sites, and processing rules so ERV reflects your real operating model.',
            deliverables: ['Field and access-rule mapping', 'Export/API model', 'Technical readiness checklist'],
          },
          {
            title: '3) Technical setup and validation',
            duration: 'Day 4-5',
            description:
              'We prepare the environment, connect devices, and close blockers before pilot traffic.',
            deliverables: ['Configured ERV tenant', 'Connected devices and sites', 'Validated IN/OUT test events'],
          },
          {
            title: '4) Controlled pilot rollout',
            duration: 'Week 2',
            description:
              'Pilot starts on limited scope (one location or one team) so accuracy gaps are resolved before full rollout.',
            deliverables: ['Pilot KPI report', 'Validated attendance and access rights', 'Final adjustment backlog'],
          },
          {
            title: '5) Production and operating routine',
            duration: 'Week 3+',
            description:
              'System moves into daily usage without business interruption while we monitor the first payroll cycles.',
            deliverables: ['Go-live without disruption', 'Operational playbook', 'Support and escalation plan'],
          },
        ],
      },
      visibility: {
        title: 'What partners and clients can see at all times',
        subtitle: 'Rollout transparency is what keeps implementation fast and low-risk.',
        metrics: [
          { label: 'Step status visibility', display: '100%', progress: 100, hint: 'Each step has owner, deadline, and completion criteria.' },
          { label: 'Pilot without disruption', display: '95%', progress: 95, hint: 'We begin with minimum safe scope.' },
          { label: 'Time to stable routine', display: '2–3 wks', progress: 75, hint: 'Typically two to three weeks to a full operating routine.' },
        ],
      },
      expectations: {
        title: 'What to expect from us',
        subtitle:
          'From day one to go-live, we run implementation so operations and leadership always share the same view.',
        cards: [
          {
            title: 'No unnecessary jargon',
            description: 'Communication stays practical: what is done, what is next, and where risk exists.',
          },
          {
            title: 'No big-bang rollout',
            description: 'We validate on pilot scope first, then scale with confidence.',
          },
          {
            title: 'No unclear ownership',
            description: 'Each phase has explicit owners on both sides, with clear completion criteria.',
          },
          {
            title: 'Free training and support',
            description:
              'Employee training, customer support, and technical support are fully free after go-live.',
          },
          {
            title: '2-year equipment warranty',
            description:
              'All equipment we deliver as part of implementation includes a 24-month warranty and clear service flow.',
          },
        ],
      },
    },
    finalCta: {
      title: 'If you want a rollout with clear deadlines and no improvisation, we are ready.',
      subtitle: 'Share your scenario and get a concrete plan in 48h: pilot scope, timeline, and technical prerequisites.',
      primaryLabel: 'Book a discovery call',
      qualifierChips: ['Number of sites and shifts', 'Free training and support', '2-year equipment warranty'],
    },
  },
} as const satisfies Record<'sr' | 'en', HowWeWorkPageContent>;
